import { useContext, useEffect, useReducer } from 'react';

import { ViewerContext } from '@vimeo/viewer';

import { Capability, UserCapabilities } from './capabilityTypes';
import { fetchAndFormatCapabilties } from './fetch-and-format-capabilities';
import { reducer } from './reducer';
import { useDeepMemo } from './useDeepMemo';
import { UserCache } from './UserCache';
import { getCapabilitiesByStatus, getUserId } from './utils';

/**
 * This is for backwards compatibility since there are other places in the codebase
 * that import `fetchAndFormatCapabilties` from this file. I will open a follow-up PR
 * to remove this export once all references are updated.
 */
export { fetchAndFormatCapabilties };

/**
 * The userCache is a singleton that contains a capabilitiesCache and a queuedRequest
 * for each user for which we call `useCapability`. These objects allow us to
 * prevent API calls if we already have the capability data we need. They also
 * allow us to prevent duplicate simultaneous API requests for the same capabilities
 * and to batch up multiple invocations of `useCapability` into a single API request.
 */
const userCache = new UserCache();

/**
 * @param requestedCapabilities An array of user capabilities to be requested
 * @param userUri
 * @param cache supply your own instance of the capabilites cache. Primarily used for testing
 *
 * @returns An object with the requested capabilites, a loading state for when the capabilites are available, and an error state if something goes wrong (default null)
 */
export function useCapability(
  requestedCapabilities: Capability[],
  userUri?: string | number | null,
  cache?: UserCache,
) {
  const viewer = useContext(ViewerContext);
  const userId = userUri ? getUserId(`${userUri}`) : viewer?.user?.id;
  const [state, dispatch] = useReducer(reducer, { loading: false, error: null, ready: false });
  const queuedRequest = (cache ?? userCache).getQueuedRequest(userId);
  const capabilitiesCache = (cache ?? userCache).getCapabilities(userId);
  const requestedCapabilitiesMemo = useDeepMemo(requestedCapabilities);

  useEffect(() => {
    if (!viewer || !userId || !capabilitiesCache || !queuedRequest) {
      return;
    }

    // Determine the status of the requested capabilities
    const { missing, queued } = getCapabilitiesByStatus(
      requestedCapabilitiesMemo,
      capabilitiesCache,
    );

    // If none of the requested capabilities are missing or queued, then dispatch
    // success and get the heck out of here.
    if (!missing.length && !queued.length) {
      dispatch({ type: 'SUCCESS' });
      return;
    } else {
      // However, if we're looking for capabilities that are either missing or
      // queued, then we need to save this dispatch for later.
      queuedRequest.addSubscriber(dispatch);
      queuedRequest.getSubscribers().forEach((dispatch) => dispatch({ type: 'LOADING' }));
    }

    // If any of the capabilities we're looking for are missing, then we need
    // to add them to the queue, clear the old request, and set up a new one.
    // In this way, we're able to batch multiple calls to `useCapability` into a
    // single API request.
    if (missing.length) {
      queuedRequest.clearTimeout();
      queuedRequest.addCapabilities(missing);
      missing.forEach((c) => {
        capabilitiesCache.update({ [c]: 'queued' });
      });

      queuedRequest.setTimeoutId(
        setTimeout(async () => {
          // Get the data we need from the queuedRequest prior to clearing it.
          const subscribers = queuedRequest.getSubscribers();
          const capabilitiesToFetch = queuedRequest.getCapabilities();

          // Since we're now executing the request, clear the queued request.
          // It's no longer queued!
          queuedRequest.clear();

          try {
            const response = await fetchAndFormatCapabilties({
              capabilities: capabilitiesToFetch,
              userId,
              jwt: viewer.jwt,
              apiUrl: viewer.apiUrl,
            });

            capabilitiesCache.update(response);
            subscribers.forEach((dispatch) => dispatch({ type: 'SUCCESS' }));
          } catch (error) {
            capabilitiesCache.remove(capabilitiesToFetch);
            subscribers.forEach((dispatch) => dispatch({ type: 'ERROR', error }));
          }
        }, 1),
      );
    }
  }, [capabilitiesCache, queuedRequest, requestedCapabilitiesMemo, userId, viewer]);

  // If user not logged in explictly return `false` for the requested capabilities. note: does not cache these values
  const capabilities =
    capabilitiesCache?.getAll() ??
    requestedCapabilities.reduce<UserCapabilities>((caps, cap) => {
      caps[cap] = false;
      return caps;
    }, {});

  return { capabilities, loading: state.loading, error: state.error, ready: state.ready };
}
